import { useCallback, useEffect, useState } from "react";
import { Archive, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { DateInput } from "@/components/ui/DateInput";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import {
  deleteChamadosArquivados,
  fetchChamadosArquivados,
  type ChamadoArquivadoDto,
  type ChamadoTipo,
} from "@/services/chamados";
import { formatDateTime } from "@/utils/format";

interface Props {
  open: boolean;
  onClose: () => void;
}

const TIPO_OPTIONS = [
  { value: "bug", label: "Bug" },
  { value: "duvida", label: "Dúvida" },
  { value: "acesso", label: "Acesso" },
  { value: "sugestao", label: "Sugestão" },
];

export function ChamadosArquivadosModal({ open, onClose }: Props) {
  const [items, setItems] = useState<ChamadoArquivadoDto[]>([]);
  const [tipo, setTipo] = useState<ChamadoTipo | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const chamados = await fetchChamadosArquivados({ tipo, dateFrom, dateTo });
      setItems(chamados);
      setSelectedIds((previous) => {
        const validIds = new Set(chamados.map((chamado) => chamado.id));
        return new Set([...previous].filter((id) => validIds.has(id)));
      });
    } catch (loadError) {
      const message =
        (loadError as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Não foi possível carregar os chamados arquivados.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [tipo, dateFrom, dateTo]);

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      void load();
    }
  }, [open, load]);

  const allSelected = items.length > 0 && selectedIds.size === items.length;

  const toggleSelected = (id: string) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(items.map((item) => item.id)));
  };

  const handleDeleteSelected = async () => {
    setDeleting(true);
    setError("");
    try {
      await deleteChamadosArquivados([...selectedIds]);
      setConfirmOpen(false);
      setSelectedIds(new Set());
      await load();
    } catch (deleteError) {
      setConfirmOpen(false);
      const message =
        (deleteError as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Não foi possível excluir os chamados selecionados.";
      setError(message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Arquivos de chamados TI" size="xl">
      <div className="space-y-4">
        <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-4">
          <Select
            label="Tipo de chamado"
            placeholder="Todos os tipos"
            options={TIPO_OPTIONS}
            value={tipo}
            onChange={(event) => setTipo(event.target.value as ChamadoTipo | "")}
          />
          <DateInput label="Data inicial" value={dateFrom} onChange={setDateFrom} />
          <DateInput label="Data final" value={dateTo} onChange={setDateTo} />
          <Button
            type="button"
            variant="secondary"
            onClick={() => void load()}
            loading={loading}
            leftIcon={<RefreshCw className="h-4 w-4" />}
          >
            Filtrar
          </Button>
        </div>

        {error && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}

        {items.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                checked={allSelected}
                onChange={toggleSelectAll}
              />
              Selecionar todos ({items.length})
            </label>
            <Button
              type="button"
              variant="danger"
              size="sm"
              disabled={selectedIds.size === 0}
              onClick={() => setConfirmOpen(true)}
              leftIcon={<Trash2 className="h-4 w-4" />}
            >
              Excluir selecionados ({selectedIds.size})
            </Button>
          </div>
        )}

        <div className="max-h-[58vh] space-y-3 overflow-y-auto rounded-xl bg-slate-50 p-3">
          {!loading && items.length === 0 && (
            <div className="py-12 text-center text-sm text-slate-500">
              <Archive className="mx-auto mb-2 h-8 w-8 text-slate-300" />
              Nenhum chamado arquivado encontrado.
            </div>
          )}
          {items.map((item) => (
            <article
              key={item.id}
              className={`rounded-xl border bg-white p-4 ${
                selectedIds.has(item.id) ? "border-red-300 ring-1 ring-red-200" : "border-slate-200"
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 cursor-pointer rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    checked={selectedIds.has(item.id)}
                    onChange={() => toggleSelected(item.id)}
                    aria-label={`Selecionar chamado ${item.titulo}`}
                  />
                  <div>
                    <h3 className="font-semibold text-slate-800">{item.titulo}</h3>
                    <p className="mt-1 text-sm text-slate-600">{item.descricao}</p>
                  </div>
                </div>
                <span className="rounded-full bg-brand-50 px-2 py-1 text-xs font-semibold text-brand-700">
                  {TIPO_OPTIONS.find((option) => option.value === item.tipo)?.label ?? item.tipo}
                </span>
              </div>
              <div className="mt-3 grid gap-1 text-xs text-slate-500 sm:grid-cols-2">
                <span>Solicitante: {item.autorNome}</span>
                <span>Módulo: {item.modulo}</span>
                <span>Criado em: {formatDateTime(item.createdAt)}</span>
                <span>
                  Arquivado em: {formatDateTime(item.arquivadoAt)} por {item.arquivadoPorNome}
                </span>
              </div>
              <details className="mt-3 border-t border-slate-100 pt-3">
                <summary className="cursor-pointer text-xs font-semibold text-brand-700">
                  Ver histórico ({item.historico?.length ?? 0})
                </summary>
                <div className="mt-2 space-y-2">
                  {(item.historico ?? []).map((entry) => (
                    <div key={entry.id} className="rounded-lg bg-slate-50 px-3 py-2 text-xs">
                      <p className="font-medium text-slate-700">{entry.descricao}</p>
                      <p className="mt-0.5 text-slate-400">
                        {entry.autorNome} · {formatDateTime(entry.createdAt)}
                      </p>
                    </div>
                  ))}
                  {!item.historico?.length && (
                    <p className="text-xs text-slate-400">
                      Chamado anterior à implantação do histórico.
                    </p>
                  )}
                </div>
              </details>
            </article>
          ))}
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmOpen}
        title="Excluir chamados arquivados"
        message={`Excluir permanentemente ${selectedIds.size} chamado(s) arquivado(s)?\nEsta ação não pode ser desfeita.`}
        confirmLabel="Excluir permanentemente"
        loading={deleting}
        onConfirm={handleDeleteSelected}
        onCancel={() => setConfirmOpen(false)}
      />
    </Modal>
  );
}
